import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { open } from "../../../store/notificationSlice";
import YouTubeEmbed from "../../../components/YouTubeEmbed";
import SettingServices from "../../../api/setting";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Sortable Row Component
const SortableRow = ({ video, onPreview, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          mb: 1,
          cursor: "grab",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          {/* Thumbnail */}
          <Box sx={{ width: 120, height: 90, mr: 2 }}>
            {video.type === "youtube" ? (
              <img
                src={video.thumbnail}
                alt={video.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 4,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "grey.200",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                }}
              >
                <PlayIcon color="action" />
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" noWrap>
              {video.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {video.description || "Không có mô tả"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Chip
                label={video.isPublic ? "Công khai" : "Ẩn"}
                color={video.isPublic ? "success" : "default"}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onPreview(video)}
            color="info"
            title="Xem trước"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onEdit(video)}
            color="primary"
            title="Chỉnh sửa"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(video)}
            color="error"
            title="Xóa"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    </div>
  );
};

SortableRow.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    isPublic: PropTypes.bool.isRequired,
  }).isRequired,
  onPreview: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const VideoManagement = () => {
  const dispatch = useDispatch();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [isDragMode, setIsDragMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Form state
  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "youtube", // only "youtube"
    video: null,
    tags: [],
    isPublic: true,
  });

  const [formErrors, setFormErrors] = useState({});

  const columns = [
    {
      field: "thumbnail",
      headerName: "Hình ảnh",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.row.type === "youtube" ? (
            <img
              src={params.row.thumbnail}
              alt={params.row.title}
              style={{
                width: 120,
                height: 90,
                objectFit: "cover",
                borderRadius: 4,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 120,
                height: 90,
                bgcolor: "grey.200",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
              }}
            >
              <PlayIcon color="action" />
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "Tiêu đề",
      width: 300,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.title}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Mô tả",
      width: 400,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.description || "Không có mô tả"}
        </Typography>
      ),
    },
    {
      field: "isPublic",
      headerName: "Trạng thái",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.isPublic ? "Công khai" : "Ẩn"}
          color={params.row.isPublic ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Thao tác",
      width: 200,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handlePreview(params.row)}
            color="info"
            title="Xem trước"
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
            title="Chỉnh sửa"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            color="error"
            title="Xóa"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const handleVideoChange = (videoData) => {
    setForm({
      ...form,
      video: videoData,
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!form.title.trim()) {
      errors.title = "Tiêu đề là bắt buộc";
    }

    if (!form.video) {
      errors.video = "Video là bắt buộc";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        type: form.type,
        tags: form.tags,
        isPublic: form.isPublic,
        ...form.video,
      };

      if (editingVideo) {
        // Update: Tìm video cũ và thay thế
        const updatedVideos = videos.map((video) =>
          video.id === editingVideo.id
            ? {
                ...video, // Giữ lại thông tin video cũ
                ...payload, // Override với thông tin mới
                id: editingVideo.id,
              }
            : video
        );
        await SettingServices.updateDataWithKey("videos", {
          value: JSON.stringify({ videos: updatedVideos }),
        });
        dispatch(
          open({
            isNoti: true,
            message: "Cập nhật video thành công",
          })
        );
      } else {
        // Create: Thêm video mới vào cuối danh sách
        const newVideo = { ...payload, id: Date.now() };
        const updatedVideos = [...videos, newVideo];
        await SettingServices.updateDataWithKey("videos", {
          value: JSON.stringify({ videos: updatedVideos }),
        });
        dispatch(
          open({
            isNoti: true,
            message: "Tạo video thành công",
          })
        );
      }

      handleCloseDrawer();
      fetchVideos();
    } catch (error) {
      console.error("Error saving video:", error);
      dispatch(
        open({
          isNoti: true,
          message: "Lỗi khi lưu video",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setForm({
      title: video.title,
      description: video.description || "",
      type: video.type,
      tags: video.tags || [],
      isPublic: video.isPublic,
      video: {
        url: video.url,
        videoId: video.videoId,
        title: video.title,
        thumbnail: video.thumbnail,
        author: video.author,
        embedUrl: video.embedUrl,
        originalUrl: video.originalUrl,
        filename: video.filename,
        size: video.size,
        type: video.videoType,
        duration: video.duration,
      },
    });
    setShowDrawer(true);
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setDeleteDialog(true);
  };

  const handlePreview = (video) => {
    setPreviewVideo(video);
    setPreviewDialog(true);
  };

  const handleClosePreview = () => {
    setPreviewDialog(false);
    setPreviewVideo(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = videos.findIndex((video) => video.id === active.id);
      const newIndex = videos.findIndex((video) => video.id === over.id);

      const reorderedVideos = arrayMove(videos, oldIndex, newIndex);
      setVideos(reorderedVideos);

      // Save new order to server
      try {
        await SettingServices.updateDataWithKey("videos", {
          value: JSON.stringify({ videos: reorderedVideos }),
        });
        dispatch(
          open({
            isNoti: true,
            message: "Cập nhật thứ tự video thành công",
          })
        );
      } catch (error) {
        console.error("Error updating video order:", error);
        dispatch(
          open({
            isNoti: true,
            message: "Lỗi khi cập nhật thứ tự video",
          })
        );
        // Revert on error
        fetchVideos();
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!videoToDelete) return;

    setLoading(true);
    try {
      // Delete: Lọc bỏ video cần xóa khỏi danh sách
      const updatedVideos = videos.filter(
        (video) => video.id !== videoToDelete.id
      );
      await SettingServices.updateDataWithKey("videos", {
        value: JSON.stringify({ videos: updatedVideos }),
      });
      dispatch(
        open({
          isNoti: true,
          message: "Xóa video thành công",
        })
      );
      fetchVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
      dispatch(
        open({
          isNoti: true,
          message: "Lỗi khi xóa video",
        })
      );
    } finally {
      setLoading(false);
      setDeleteDialog(false);
      setVideoToDelete(null);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingVideo(null);
    setForm({
      title: "",
      description: "",
      type: "youtube",
      video: null,
      tags: [],
      isPublic: true,
    });
    setFormErrors({});
  };

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const response = await SettingServices.getDataWithKey("videos");
      setVideos(response.data.value?.videos || []);
    } catch (error) {
      console.error("Error fetching videos:", error);
      dispatch(
        open({
          isNoti: true,
          message: "Error fetching videos",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [searchTerm]);

  const filteredVideos = videos.filter((video) => {
    const matchesSearch = video.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <Box p={3}>
      <Typography variant="h5" my={1} fontWeight="bold">
        Quản lý Video
      </Typography>
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        spacing={2}
        my={2}
      >
        <Button onClick={() => setShowDrawer(true)} variant="contained">
          Thêm Video Mới
        </Button>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          label="Tìm kiếm video"
          placeholder="Tìm kiếm video..."
          className="w-[300px]"
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
            ),
          }}
        />
        <Button
          variant={isDragMode ? "contained" : "outlined"}
          onClick={() => setIsDragMode(!isDragMode)}
          startIcon={<FilterListIcon />}
        >
          {isDragMode ? "Thoát chế độ kéo" : "Sắp xếp Video"}
        </Button>
      </Stack>

      {/* Video List with Drag & Drop */}
      <Box sx={{ width: "100%" }}>
        {isDragMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredVideos.map((video) => video.id)}
              strategy={verticalListSortingStrategy}
            >
              {filteredVideos.map((video) => (
                <SortableRow
                  key={video.id}
                  video={video}
                  onPreview={handlePreview}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <DataGrid
            rows={filteredVideos}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowId={(row) => row.id}
            getRowHeight={() => 120}
          />
        )}
      </Box>

      {/* Add/Edit Drawer */}
      <Dialog
        open={showDrawer}
        onClose={handleCloseDrawer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingVideo ? "Chỉnh sửa Video" : "Thêm Video Mới"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tiêu đề"
                  name="title"
                  value={form.title}
                  onChange={handleFormChange}
                  error={!!formErrors.title}
                  helperText={formErrors.title}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mô tả"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Trạng thái</InputLabel>
                  <Select
                    name="isPublic"
                    value={form.isPublic}
                    onChange={handleFormChange}
                    label="Trạng thái"
                  >
                    <MenuItem value={true}>Công khai</MenuItem>
                    <MenuItem value={false}>Ẩn</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <YouTubeEmbed
                  value={form.video}
                  onChange={handleVideoChange}
                  error={formErrors.video}
                  form={form}
                  setForm={setForm}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDrawer}>Hủy</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? "Đang lưu..." : editingVideo ? "Cập nhật" : "Tạo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa &quot;{videoToDelete?.title}&quot;? Hành
            động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Hủy</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Đang xóa..." : "Xóa"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{previewVideo?.title}</DialogTitle>
        <DialogContent>
          {previewVideo && (
            <Box>
              <iframe
                width="100%"
                height="400"
                src={previewVideo.embedUrl}
                title={previewVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {previewVideo.description}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Chip label="YouTube" color="primary" />
                <Chip
                  label={previewVideo.isPublic ? "Public" : "Private"}
                  color={previewVideo.isPublic ? "success" : "default"}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Đóng</Button>
          <Button
            startIcon={<LinkIcon />}
            onClick={() => window.open(previewVideo?.originalUrl, "_blank")}
          >
            Mở trên YouTube
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoManagement;
